import { supabase } from "../supabaseClient";

const getActiveTenantId = () => {
  const tenantId = window.localStorage.getItem('activeTenantId');
  return tenantId ? String(tenantId).trim() : '';
};

function requireActiveTenantId() {
  const tenantId = getActiveTenantId();
  if (!tenantId) {
    throw new Error('No active tenant selected.');
  }
  return tenantId;
}

const REVIEW_ACTIONS = {
  SEND_FOR_REVIEW: "send_for_review",
  START_REVIEW: "start_review",
  REQUEST_AMENDMENTS: "request_amendments",
  CONFIRM_AMENDMENTS: "confirm_amendments",
  MARK_READY_FOR_SIGNOFF: "mark_ready_for_signoff",
  SIGN_OFF_RELEASE: "sign_off_release",
};

function buildDrawingNameMap(drawings) {
  const map = {};
  (drawings || []).forEach((row) => {
    map[row.id] = {
      name: row.display_name || row.filename || row.id,
      version: row.version_number || null,
    };
  });
  return map;
}

function toReviewRow(task, drawingNameById = {}, openDesignChangeCountByTaskId = {}, progressByTaskId = {}) {
  const links = task.task_links || [];
  const drawingLink = links.find((link) => link.entity_type === "drawing");

  return {
    id: task.id,
    taskId: task.id,
    title: task.title,
    projectId: task.project_id,
    projectName: task.projects?.name || "Unknown Project",
    linkedTask: task.title,
    createdAt: task.created_at || null,
    status: task.status,
    stage: task.review_stage || "Designer Phase",
    drawingId: drawingLink?.entity_id || null,
    drawingName: drawingLink?.entity_id
      ? drawingNameById[drawingLink.entity_id]?.name || drawingLink.entity_id
      : "No drawing linked",
    drawingRevision: drawingLink?.entity_id
      ? drawingNameById[drawingLink.entity_id]?.version || "N/A"
      : "N/A",
    designerId: task.assigned_to || null,
    designer: task.assignee?.display_name || [task.assignee?.first_name, task.assignee?.last_name].filter(Boolean).join(" ") || "Unassigned",
    reviewerId: task.reviewer_id || null,
    reviewer: task.reviewer?.display_name || [task.reviewer?.first_name, task.reviewer?.last_name].filter(Boolean).join(" ") || "Unassigned",
    dueDate: task.due_date,
    cycleCount: Number(task.amendment_cycle_count || 0),
    submittedForReviewAt: task.submitted_for_review_at,
    submittedForReviewBy: task.submitted_for_review_by_data?.display_name
      || [task.submitted_for_review_by_data?.first_name, task.submitted_for_review_by_data?.last_name].filter(Boolean).join(" ")
      || null,
    signedOffAt: task.signed_off_at,
    openLinkedDesignChanges: openDesignChangeCountByTaskId[task.id] || 0,
    progress: progressByTaskId.hasOwnProperty(task.id) ? progressByTaskId[task.id] : toPhaseProgress(task.review_stage || "Designer Phase"),
    links,
  };

  return links;
}

async function getTaskWithLinks(taskId) {
  const { data, error } = await supabase
    .from("tasks")
    .select(
      `
      id,
      project_id,
      title,
      status,
      qa_required,
      assigned_to,
      reviewer_id,
      due_date,
      completed_at,
      review_stage,
      amendment_cycle_count,
      submitted_for_review_at,
      signed_off_at,
      task_links(id, entity_type, entity_id)
    `
    )
    .eq("id", taskId)
    .single();

  if (error) throw error;
  return data;
}

async function getChecklistItemKeys(taskId, checklistId) {
  // Get the latest version for this checklist
  const { data: version, error: versionError } = await supabase
    .from("qa_checklist_versions")
    .select("id")
    .eq("checklist_id", checklistId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (versionError) throw versionError;

  if (version?.id) {
    const { data: sections, error: sectionError } = await supabase
      .from("qa_section_versions")
      .select("id")
      .eq("checklist_version_id", version.id);

    if (sectionError) throw sectionError;

    const sectionIds = (sections || []).map((row) => row.id);
    if (sectionIds.length === 0) return { field: "item_version_id", keys: [] };

    const { data: items, error: itemError } = await supabase
      .from("qa_item_versions")
      .select("id")
      .in("section_version_id", sectionIds);

    if (itemError) throw itemError;

    return { field: "item_version_id", keys: (items || []).map((row) => row.id) };
  }

  const { data: sections, error: sectionError } = await supabase
    .from("qa_sections")
    .select("id")
    .eq("checklist_id", checklistId);

  if (sectionError) throw sectionError;

  const sectionIds = (sections || []).map((row) => row.id);
  if (sectionIds.length === 0) return { field: "item_id", keys: [] };

  const { data: items, error: itemError } = await supabase
    .from("qa_items")
    .select("id")
    .in("section_id", sectionIds);

  if (itemError) throw itemError;

  return { field: "item_id", keys: (items || []).map((row) => row.id) };
}

async function getChecklistCompletion(reviewChecklistId, checklistId) {
  const { field, keys } = await getChecklistItemKeys(null, checklistId);
  if (keys.length === 0) return { designerComplete: false, reviewerComplete: false };

  const { data: responses, error } = await supabase
    .from("qa_responses")
    .select("item_id, item_version_id, response, verifier_response")
    .eq("project_checklist_id", reviewChecklistId);

  if (error) throw error;

  const keyToResponse = new Map();
  const keyToVerifierResponse = new Map();

  (responses || []).forEach((row) => {
    const key = field === "item_version_id" ? row.item_version_id : row.item_id;
    if (!key) return;
    keyToResponse.set(key, row.response);
    keyToVerifierResponse.set(key, row.verifier_response);
  });

  const designerComplete = keys.every((key) => Boolean(keyToResponse.get(key)));
  const reviewerComplete = keys.every((key) => Boolean(keyToVerifierResponse.get(key)));

  return { designerComplete, reviewerComplete };
}

async function getTaskReviewProjectChecklists(task) {
  const { data: reviewChecklistLinks, error: reviewChecklistError } = await supabase
    .from("qa_review_checklists")
    .select("id, checklist_id")
    .eq("task_id", task.id);

  if (reviewChecklistError) throw reviewChecklistError;

  if (!reviewChecklistLinks || reviewChecklistLinks.length === 0) {
    return [];
  }

  const checklistIds = Array.from(
    new Set(reviewChecklistLinks.map((row) => row.checklist_id).filter(Boolean))
  );

  // Query qa_checklists to get titles
  const { data: checklistRows, error: checklistRowsError } = await supabase
    .from("qa_checklists")
    .select("id, title")
    .in("id", checklistIds);

  if (checklistRowsError) throw checklistRowsError;

  // Get the latest version for each checklist
  const { data: versionRows, error: versionError } = await supabase
    .from("qa_checklist_versions")
    .select("id, checklist_id, version_label")
    .in("checklist_id", checklistIds)
    .order("created_at", { ascending: false });

  if (versionError) throw versionError;

  // Get the latest version for each checklist
  const latestVersionByChecklistId = {};
  (versionRows || []).forEach((row) => {
    if (!latestVersionByChecklistId[row.checklist_id]) {
      latestVersionByChecklistId[row.checklist_id] = row;
    }
  });

  // Create a map of review_checklist_id to checklist_id
  const reviewChecklistById = {};
  reviewChecklistLinks.forEach((row) => {
    reviewChecklistById[row.id] = row.checklist_id;
  });

  // Combine the data - use qa_review_checklist id as the id
  return reviewChecklistLinks.map((reviewChecklist) => {
    const checklist = checklistRows?.find(c => c.id === reviewChecklist.checklist_id);
    const version = latestVersionByChecklistId[reviewChecklist.checklist_id];
    return {
      id: reviewChecklist.id, // Use the actual qa_review_checklist id
      project_id: task.project_id,
      checklist_id: reviewChecklist.checklist_id,
      checklist_version_id: version?.id || null,
      qa_checklist: checklist ? { id: checklist.id, title: checklist.title } : null,
    };
  }).filter(row => row.qa_checklist);
}

async function getTaskChecklistCompletion(task) {
  const projectChecklists = await getTaskReviewProjectChecklists(task);

  if (projectChecklists.length === 0) {
    return {
      projectChecklists: [],
      designerComplete: false,
      reviewerComplete: false,
    };
  }

  const completionRows = await Promise.all(
    projectChecklists.map(async (row) => {
      const completion = await getChecklistCompletion(row.id, row.checklist_id);
      return {
        projectChecklistId: row.id,
        designerComplete: completion.designerComplete,
        reviewerComplete: completion.reviewerComplete,
      };
    })
  );

  return {
    projectChecklists,
    designerComplete: completionRows.every((row) => row.designerComplete),
    reviewerComplete: completionRows.every((row) => row.reviewerComplete),
  };
}

function normalizeChecklistValue(value) {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim().toLowerCase();
  return normalized || null;
}

function buildChecklistProgress(sections, designerResponses = {}, reviewerResponses = {}) {
  const itemIds = (sections || []).flatMap((section) => (section.items || []).map((item) => item.id));
  const totalItems = itemIds.length;

  const designerCompleted = itemIds.filter((itemId) => Boolean(designerResponses[itemId])).length;
  const reviewerCompleted = itemIds.filter((itemId) => Boolean(reviewerResponses[itemId])).length;

  return {
    totalItems,
    designerCompleted,
    reviewerCompleted,
    designerComplete: totalItems > 0 && designerCompleted === totalItems,
    reviewerComplete: totalItems > 0 && reviewerCompleted === totalItems,
  };
}

export async function listReviewChecklists(taskId) {
  if (!taskId) return [];

  const task = await getTaskWithLinks(taskId);
  const projectChecklists = await getTaskReviewProjectChecklists(task);
  if (projectChecklists.length === 0) return [];

  const checklistVersionIds = projectChecklists
    .map((row) => row.checklist_version_id)
    .filter(Boolean);

  let versionById = {};
  if (checklistVersionIds.length > 0) {
    const { data: versionRows, error: versionError } = await supabase
      .from("qa_checklist_versions")
      .select("id, version_label")
      .in("id", checklistVersionIds);

    if (versionError) throw versionError;

    versionById = (versionRows || []).reduce((acc, row) => {
      acc[row.id] = row;
      return acc;
    }, {});
  }

  const responsesByProjectChecklist = {};
  const projectChecklistIds = projectChecklists.map((row) => row.id);
  const { data: responseRows, error: responseError } = await supabase
    .from("qa_responses")
    .select("project_checklist_id, item_id, item_version_id, response, verifier_response, comments")
    .in("project_checklist_id", projectChecklistIds);

  if (responseError) throw responseError;

  (responseRows || []).forEach((row) => {
    if (!responsesByProjectChecklist[row.project_checklist_id]) {
      responsesByProjectChecklist[row.project_checklist_id] = {
        designer: {},
        reviewer: {},
        comments: {},
      };
    }

    const responseKey = row.item_version_id || row.item_id;
    if (!responseKey) return;

    responsesByProjectChecklist[row.project_checklist_id].designer[responseKey] = row.response;
    responsesByProjectChecklist[row.project_checklist_id].reviewer[responseKey] = row.verifier_response;
    responsesByProjectChecklist[row.project_checklist_id].comments[responseKey] = row.comments;
  });

  const checklistRows = await Promise.all(
    projectChecklists.map(async (projectChecklist) => {
      let sections = [];

      if (projectChecklist.checklist_version_id) {
        const { data: sectionRows, error: sectionError } = await supabase
          .from("qa_section_versions")
          .select("id, title, position, qa_item_versions(id, question, position)")
          .eq("checklist_version_id", projectChecklist.checklist_version_id)
          .order("position", { ascending: true });

        if (sectionError) throw sectionError;

        sections = (sectionRows || []).map((section) => ({
          id: section.id,
          title: section.title,
          position: section.position,
          items: (section.qa_item_versions || [])
            .slice()
            .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
            .map((item) => ({
              id: item.id,
              question: item.question,
              position: item.position,
            })),
        }));
      } else {
        const { data: sectionRows, error: sectionError } = await supabase
          .from("qa_sections")
          .select("id, title, position, qa_items(id, question, position)")
          .eq("checklist_id", projectChecklist.checklist_id)
          .order("position", { ascending: true });

        if (sectionError) throw sectionError;

        sections = (sectionRows || []).map((section) => ({
          id: section.id,
          title: section.title,
          position: section.position,
          items: (section.qa_items || [])
            .slice()
            .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
            .map((item) => ({
              id: item.id,
              question: item.question,
              position: item.position,
            })),
        }));
      }

      const responseState = responsesByProjectChecklist[projectChecklist.id] || {
        designer: {},
        reviewer: {},
        comments: {},
      };

      return {
        projectChecklistId: projectChecklist.id,
        checklistId: projectChecklist.checklist_id,
        checklistVersionId: projectChecklist.checklist_version_id || null,
        title: projectChecklist.qa_checklist?.title || "QA Checklist",
        versionLabel: projectChecklist.checklist_version_id
          ? versionById[projectChecklist.checklist_version_id]?.version_label || null
          : null,
        sections,
        responses: responseState,
        progress: buildChecklistProgress(sections, responseState.designer, responseState.reviewer),
      };
    })
  );

  return checklistRows;
}

export async function saveReviewChecklistResponse({
  taskId,
  projectChecklistId,
  checklistVersionId,
  itemId,
  phase,
  value,
  comments,
}) {
  if (!taskId || !projectChecklistId || !itemId) {
    throw new Error("Task, checklist, and item are required.");
  }

  if (!["designer", "reviewer"].includes(phase)) {
    throw new Error("Checklist response phase must be designer or reviewer.");
  }

  const normalizedValue = normalizeChecklistValue(value);

  // Get the review_checklist to validate and get checklist_id
  const { data: reviewChecklist, error: reviewChecklistError } = await supabase
    .from("qa_review_checklists")
    .select("id, checklist_id")
    .eq("id", projectChecklistId)
    .eq("task_id", taskId)
    .maybeSingle();

  if (reviewChecklistError || !reviewChecklist) {
    throw new Error("Review checklist not found or not linked to this task.");
  }

  // Get the latest version for this checklist
  const { data: version, error: versionError } = await supabase
    .from("qa_checklist_versions")
    .select("id")
    .eq("checklist_id", reviewChecklist.checklist_id)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (versionError) throw versionError;

  const isVersionedChecklist = Boolean(version?.id);

  const itemColumn = isVersionedChecklist ? "item_version_id" : "item_id";
  const otherItemColumn = isVersionedChecklist ? "item_id" : "item_version_id";

  const { data: existingRow, error: existingRowError } = await supabase
    .from("qa_responses")
    .select("id")
    .eq("project_checklist_id", projectChecklistId)
    .eq(itemColumn, itemId)
    .maybeSingle();

  if (existingRowError) throw existingRowError;

  const phasePayload =
    phase === "designer"
      ? { response: normalizedValue }
      : { verifier_response: normalizedValue };

  const commentsPayload = comments !== undefined ? { comments: comments || null } : {};

  if (existingRow?.id) {
    const { error: updateError } = await supabase
      .from("qa_responses")
      .update({
        ...phasePayload,
        ...commentsPayload,
      })
      .eq("id", existingRow.id);

    if (updateError) throw updateError;
    return;
  }

  const insertPayload = {
    project_checklist_id: projectChecklistId,
    response: null,
    verifier_response: null,
    [itemColumn]: itemId,
    [otherItemColumn]: null,
    ...phasePayload,
    ...commentsPayload,
  };

  const activeTenantId = getActiveTenantId();
  if (!activeTenantId) {
    throw new Error('No active tenant selected.');
  }
  insertPayload.tenant_id = activeTenantId;

  const { error: insertError } = await supabase
    .from("qa_responses")
    .insert(insertPayload);

  if (insertError) throw insertError;
}

async function ensureChecklistLink(task) {
  const existing = (task.task_links || []).find((link) => link.entity_type === "qa_checklist");
  if (existing) return existing.entity_id;

  // Get the first available checklist for this project from qa_review_checklists
  const { data: reviewChecklist, error: reviewChecklistError } = await supabase
    .from("qa_review_checklists")
    .select("id, checklist_id")
    .eq("task_id", task.id)
    .limit(1)
    .maybeSingle();

  if (reviewChecklistError || !reviewChecklist) {
    throw new Error("No review checklist is available for this review task.");
  }

  // Use the qa_review_checklist id as the entity_id
  const activeTenantId = getActiveTenantId();
  if (!activeTenantId) {
    throw new Error('No active tenant selected.');
  }

  const { error: linkError } = await supabase.from("task_links").insert({
    task_id: task.id,
    entity_type: "qa_checklist",
    entity_id: reviewChecklist.id,
    tenant_id: activeTenantId,
  });

  if (linkError) throw linkError;

  return reviewChecklist.id;
}

async function getOpenLinkedDesignChangesCount(task) {
  const designChangeIds = (task.task_links || [])
    .filter((link) => link.entity_type === "design_change")
    .map((link) => link.entity_id);

  if (designChangeIds.length === 0) return 0;

  const { count, error } = await supabase
    .from("design_changes")
    .select("id", { count: "exact", head: true })
    .in("id", designChangeIds)
    .neq("status", "closed");

  if (error) throw error;
  return count || 0;
}

function toPhaseProgress(stage) {
  switch (stage) {
    case 'Designer Phase':
      return 25;
    case 'Ready For Review':
      return 45;
    case 'Reviewer Phase':
      return 60;
    case 'Amendments':
      return 75;
    case 'Ready For Signoff':
      return 90;
    case 'Signed Off':
      return 100;
    default:
      return 20;
  }
}

export async function listProjectDrawings(projectId) {
  if (!projectId) return [];

  const activeTenantId = requireActiveTenantId();

  const { data, error } = await supabase
    .from("drawings")
    .select("id, display_name, filename, version_number")
    .eq("project_id", projectId)
    .eq("tenant_id", activeTenantId)
    .order("display_name", { ascending: true });

  if (error) throw error;

  return (data || []).map((row) => ({
    id: row.id,
    displayName: row.display_name || row.filename || row.id,
    filename: row.filename || "",
    versionNumber: row.version_number || null,
  }));
}

function assertCondition(condition, message) {
  if (!condition) throw new Error(message);
}

export async function listReviews({ projectId = null, onlyMine = false, userId = null } = {}) {
  const activeTenantId = requireActiveTenantId();
  console.log('[Progress Debug] listReviews called');
  let query = supabase
    .from("tasks")
    .select(
      `
      id,
      project_id,
      title,
      status,
      qa_required,
      assigned_to,
      reviewer_id,
      due_date,
      created_at,
      review_stage,
      amendment_cycle_count,
      submitted_for_review_at,
      submitted_for_review_by,
      signed_off_at,
      task_links(entity_type, entity_id),
      projects!inner(id, name),
      assignee:profiles!tasks_assigned_to_fkey(display_name, first_name, last_name),
      reviewer:profiles!tasks_reviewer_id_fkey(display_name, first_name, last_name),
      submitted_for_review_by_data:profiles!tasks_submitted_for_review_by_fkey(display_name, first_name, last_name)
    `
    )
    .eq("tenant_id", activeTenantId)
    .eq("qa_required", true)
    .order("created_at", { ascending: false });

  if (projectId) query = query.eq("project_id", projectId);
  if (onlyMine && userId) query = query.eq("reviewer_id", userId);

  const { data, error } = await query;
  if (error) throw error;

  const rows = data || [];
  const drawingIds = rows
    .flatMap((task) => task.task_links || [])
    .filter((link) => link.entity_type === "drawing")
    .map((link) => link.entity_id);

  let drawingNameById = {};
  if (drawingIds.length > 0) {
    const { data: drawings } = await supabase
      .from("drawings")
      .select("id, display_name, filename, version_number")
      .eq("tenant_id", activeTenantId)
      .in("id", Array.from(new Set(drawingIds)));
    drawingNameById = buildDrawingNameMap(drawings);
  }

  const openDesignChangeCountByTaskId = {};
  await Promise.all(
    rows.map(async (task) => {
      openDesignChangeCountByTaskId[task.id] = await getOpenLinkedDesignChangesCount(task);
    })
  );

  // Compute checklist-based progress for all tasks at once
  const taskIds = rows.map((r) => r.id);
  const progressByTaskId = {};
  console.log('[Progress Debug] taskIds:', taskIds);
  if (taskIds.length > 0) {
    try {
      // Fetch all qa_review_checklists for these tasks
      const { data: reviewChecklistRows } = await supabase
        .from("qa_review_checklists")
        .select("id, task_id, checklist_id")
        .in("task_id", taskIds);

      const reviewChecklistMap = {}; // task_id -> [{id, checklist_id}]
      const checklistIdSet = new Set();
      (reviewChecklistRows || []).forEach((r) => {
        if (!reviewChecklistMap[r.task_id]) reviewChecklistMap[r.task_id] = [];
        reviewChecklistMap[r.task_id].push({ id: r.id, checklistId: r.checklist_id });
        if (r.checklist_id) checklistIdSet.add(r.checklist_id);
      });

      console.log('[Progress Debug] reviewChecklistRows:', reviewChecklistRows);
      console.log('[Progress Debug] checklistIdSet size:', checklistIdSet.size);

      if (checklistIdSet.size > 0) {
        const allChecklistIds = Array.from(checklistIdSet);

        // Get latest version per checklist
        const { data: versionRows } = await supabase
          .from("qa_checklist_versions")
          .select("id, checklist_id")
          .in("checklist_id", allChecklistIds)
          .order("created_at", { ascending: false });

        const latestVersionByChecklist = {};
        (versionRows || []).forEach((v) => {
          if (!latestVersionByChecklist[v.checklist_id]) {
            latestVersionByChecklist[v.checklist_id] = v.id;
          }
        });

        // Count items per checklist
        const checklistItemCount = {};
        const versionedIds = Object.values(latestVersionByChecklist).filter(Boolean);

        // Versioned checklists
        if (versionedIds.length > 0) {
          const { data: sectionRows } = await supabase
            .from("qa_section_versions")
            .select("id, checklist_version_id, qa_item_versions(id)")
            .in("checklist_version_id", versionedIds);

          (sectionRows || []).forEach((section) => {
            const cvId = section.checklist_version_id;
            // Find the checklist_id for this version
            for (const [chkId, verId] of Object.entries(latestVersionByChecklist)) {
              if (verId === cvId) {
                checklistItemCount[chkId] = (checklistItemCount[chkId] || 0) + (section.qa_item_versions?.length || 0);
                break;
              }
            }
          });
        }

        // Non-versioned checklists
        const nonVersionedIds = allChecklistIds.filter((id) => !latestVersionByChecklist[id]);
        if (nonVersionedIds.length > 0) {
          const { data: sectionRows } = await supabase
            .from("qa_sections")
            .select("id, checklist_id, qa_items(id)")
            .in("checklist_id", nonVersionedIds);

          (sectionRows || []).forEach((section) => {
            checklistItemCount[section.checklist_id] = (checklistItemCount[section.checklist_id] || 0) + (section.qa_items?.length || 0);
          });
        }

        // Get all responses for these project_checklist_ids
        const allProjectChecklistIds = (reviewChecklistRows || []).map((r) => r.id);
        console.log('[Progress Debug] allProjectChecklistIds:', allProjectChecklistIds);

        // Try to get responses by querying all responses and filtering in memory
        // This is a workaround for potential ID mismatches
        const { data: allResponses } = await supabase
          .from("qa_responses")
          .select("project_checklist_id, response, verifier_response")
          .eq("tenant_id", activeTenantId)
          .in("project_checklist_id", allProjectChecklistIds);

        console.log('[Progress Debug] allResponses (all in table):', allResponses?.length || 0);

        const allResponseRows = allResponses || [];

        console.log('[Progress Debug] allResponseRows:', allResponseRows);

        // Compute response counts per project_checklist
        const responseCounts = {};
        (allResponseRows || []).forEach((r) => {
          if (!responseCounts[r.project_checklist_id]) {
            responseCounts[r.project_checklist_id] = { designer: 0, reviewer: 0 };
          }
          if (Boolean(r.response)) responseCounts[r.project_checklist_id].designer++;
          if (Boolean(r.verifier_response)) responseCounts[r.project_checklist_id].reviewer++;
        });

        console.log('[Progress Debug] responseCounts:', responseCounts);

        // Compute progress per task
        rows.forEach((task) => {
          const taskChecklists = reviewChecklistMap[task.id] || [];
          let totalItems = 0;
          let completedDesigner = 0;
          let completedReviewer = 0;

          console.log(`[Progress Debug] Task ${task.id} checklists:`, taskChecklists);

          taskChecklists.forEach((pc) => {
            const itemCount = checklistItemCount[pc.checklistId] || 0;
            totalItems += itemCount;
            const counts = responseCounts[pc.id];
            if (counts) {
              completedDesigner += counts.designer;
              completedReviewer += counts.reviewer;
            }
          });

          const signoffAchieved = task.review_stage === "Signed Off" || Boolean(task.signed_off_at);
          const totalMaxResponses = totalItems * 2; // designer + reviewer per item
          const checklistScore = totalMaxResponses > 0
            ? ((completedDesigner + completedReviewer) / totalMaxResponses) * 90
            : 0;
          const signoffScore = signoffAchieved ? 10 : 0;
          progressByTaskId[task.id] = Math.round(checklistScore + signoffScore);

          console.log(`[Progress Debug] Task ${task.id} progress:`, {
            totalItems,
            completedDesigner,
            completedReviewer,
            totalMaxResponses,
            checklistScore,
            signoffScore,
            finalProgress: progressByTaskId[task.id]
          });
        });
      }
    } catch (e) {
      // If batch progress computation fails, fall back silently to stage-based progress
      console.warn("Failed to compute checklist progress for reviews table:", e);
    }
  }

  console.log('[Progress Debug] progressByTaskId before mapping:', progressByTaskId);
  return rows.map((task) => toReviewRow(task, drawingNameById, openDesignChangeCountByTaskId, progressByTaskId));
}

export async function transitionReview({ taskId, action, actorId, comment }) {
  const task = await getTaskWithLinks(taskId);

  assertCondition(task.qa_required, "This task is not configured as a review task.");

  const checklistCompletion = await getTaskChecklistCompletion(task);
  const checklistId = checklistCompletion.projectChecklists[0]?.id || (await ensureChecklistLink(task));

  if (action === REVIEW_ACTIONS.SEND_FOR_REVIEW) {
    assertCondition(task.reviewer_id, "Assign a reviewer before sending for review.");
    assertCondition(checklistCompletion.designerComplete, "Designer checklist is incomplete.");

    assertCondition(Boolean(actorId), "Actor id is required to send for review.");

    const { error } = await supabase
      .from("tasks")
      .update({
        status: "Ready For Review",
        review_stage: "Ready For Review",
        submitted_for_review_at: new Date().toISOString(),
        submitted_for_review_by: actorId,
      })
      .eq("id", taskId);

    if (error) throw error;
    return;
  }

  if (action === REVIEW_ACTIONS.START_REVIEW) {
    assertCondition(task.reviewer_id, "A reviewer must be assigned.");
    if (actorId) {
      assertCondition(task.reviewer_id === actorId, "Only the assigned reviewer can start review.");
    }

    const { error } = await supabase
      .from("tasks")
      .update({ status: "In Review", review_stage: "Reviewer Phase" })
      .eq("id", taskId);

    if (error) throw error;
    return;
  }

  if (action === REVIEW_ACTIONS.REQUEST_AMENDMENTS) {
    assertCondition(task.status === "In Review", "Task must be In Review to request amendments.");

    // Post the comment before transitioning, if provided
    if (comment && actorId) {
      await postAmendmentComment({ taskId, commentText: comment, authorId: actorId });
    }

    const { error } = await supabase
      .from("tasks")
      .update({
        status: "Amendments Required",
        review_stage: "Amendments",
        amendment_cycle_count: Number(task.amendment_cycle_count || 0) + 1,
      })
      .eq("id", taskId);

    if (error) throw error;
    return;
  }

  if (action === REVIEW_ACTIONS.CONFIRM_AMENDMENTS) {
    assertCondition(task.status === "Amendments Required", "Task must be Amendments Required.");

    // Post the comment before transitioning, if provided
    if (comment && actorId) {
      await postAmendmentComment({ taskId, commentText: comment, authorId: actorId });
    }

    const { error } = await supabase
      .from("tasks")
      .update({ status: "In Review", review_stage: "Reviewer Phase" })
      .eq("id", taskId);

    if (error) throw error;
    return;
  }

  if (action === REVIEW_ACTIONS.MARK_READY_FOR_SIGNOFF) {
    assertCondition(task.status === "In Review", "Task must be In Review.");
    assertCondition(checklistCompletion.reviewerComplete, "Reviewer checklist is incomplete.");

    const openDesignChanges = await getOpenLinkedDesignChangesCount(task);
    assertCondition(openDesignChanges === 0, "There are open linked design changes.");

    const { error } = await supabase
      .from("tasks")
      .update({ status: "Ready For Signoff", review_stage: "Ready For Signoff" })
      .eq("id", taskId);

    if (error) throw error;
    return;
  }

  if (action === REVIEW_ACTIONS.SIGN_OFF_RELEASE) {
    assertCondition(task.status === "Ready For Signoff", "Task must be Ready For Signoff.");

    let signoffId = null;

    const { data: existingSignoff, error: existingError } = await supabase
      .from("qa_signoffs")
      .select("id")
      .eq("project_checklist_id", checklistId)
      .eq("role", "verifier_ready")
      .maybeSingle();

    if (existingError) throw existingError;

    if (existingSignoff?.id) {
      signoffId = existingSignoff.id;
    } else {
      assertCondition(Boolean(actorId), "Actor id is required to sign off.");

      const { data: newSignoff, error: signoffError } = await supabase
        .from("qa_signoffs")
        .insert({
          project_checklist_id: checklistId,
          role: "verifier_ready",
          signed_by: actorId,
          signed_at: new Date().toISOString(),
        })
        .select("id")
        .single();

      if (signoffError) throw signoffError;
      signoffId = newSignoff.id;
    }

    const { error: linkError } = await supabase
      .from("task_links")
      .upsert(
        { task_id: task.id, entity_type: "qa_signoff", entity_id: signoffId },
        { onConflict: "task_id,entity_type,entity_id" }
      );

    if (linkError) throw linkError;

    const nowIso = new Date().toISOString();
    const { error: updateError } = await supabase
      .from("tasks")
      .update({
        status: "Completed",
        review_stage: "Signed Off",
        completed_at: nowIso,
        signed_off_at: nowIso,
      })
      .eq("id", taskId);

    if (updateError) throw updateError;
    return;
  }

  throw new Error(`Unsupported review action: ${action}`);
}

export async function postAmendmentComment({ taskId, commentText, authorId }) {
  if (!taskId || !commentText || !authorId) {
    throw new Error("taskId, commentText, and authorId are required.");
  }

  const { data, error } = await supabase
    .from("review_amendment_comments")
    .insert({
      task_id: taskId,
      author_id: authorId,
      comment_text: commentText,
    })
    .select("id")
    .single();

  if (error) throw error;
  return data;
}

export async function listAmendmentComments(taskId) {
  if (!taskId) return [];

  const { data, error } = await supabase
    .from("review_amendment_comments")
    .select(
      `
      id,
      comment_text,
      created_at,
      author_id,
      author:profiles!review_amendment_comments_author_id_fkey(display_name, first_name, last_name)
    `
    )
    .eq("task_id", taskId)
    .order("created_at", { ascending: true });

  if (error) throw error;

  return (data || []).map((row) => ({
    id: row.id,
    commentText: row.comment_text,
    createdAt: row.created_at,
    authorId: row.author_id,
    authorName:
      row.author?.display_name ||
      [row.author?.first_name, row.author?.last_name].filter(Boolean).join(" ") ||
      "Unknown",
  }));
}

export { REVIEW_ACTIONS };
